import { TFunction } from 'i18next'

import type { Service, ServiceBookingType, ServiceSku } from '@/api/services'
import { formatDollars } from '@/utils/currency'

import {
  NO_TRAINING_TYPE_CATEGORIES,
  SERVICE_CATEGORY_LABEL_KEYS,
  ServiceCategory,
  TIMED_CATEGORIES,
} from './services.constants'

// Note: the category is free text on the BE, so an unknown one falls back to its raw value
export const getCategoryLabel = (t: TFunction<['organisation', 'common']>, category: string) =>
  category in SERVICE_CATEGORY_LABEL_KEYS
    ? t(
        `organisation:servicesPage.categories.${SERVICE_CATEGORY_LABEL_KEYS[category as ServiceCategory]}`,
      )
    : category

export const isTimedCategory = (category: string) => TIMED_CATEGORIES.includes(category)

export const sortServices = (services: Service[]) =>
  [...services].sort((a, b) => a.name.localeCompare(b.name, undefined, { numeric: true }))

export const sortSkus = (skus: ServiceSku[]) =>
  [...skus].sort(
    (a, b) =>
      (a.duration ?? Number.MAX_SAFE_INTEGER) - (b.duration ?? Number.MAX_SAFE_INTEGER) ||
      a.name.localeCompare(b.name, undefined, { numeric: true }),
  )

export const getBookingTypeRef = (service: Service): ServiceBookingType | null => {
  const { bookingType } = service
  if (bookingType === null || bookingType === undefined) return null
  return typeof bookingType === 'number'
    ? { id: bookingType, name: String(bookingType) }
    : bookingType
}

export const formatCost = (cost: number) => formatDollars(Number(cost), { zeroPrice: true })

// Note: the BE names the training type of a subservice "<service name> <duration> min" (SkuService.create)
export const getTrainingTypeName = (serviceName: string, duration: number | string | null) =>
  `${serviceName} ${duration} min`

export type TrainingTypeStatus =
  | { created: true; name: string }
  | { created: false; reason: 'missingVisuals' | 'notApplicable' }

// Note: mirrors the BE rule - a training type is created only for a service that has both an icon and
// a color and is not in a category without training types
export const getTrainingTypeStatus = (
  service: Pick<Service, 'category' | 'color' | 'icon' | 'name'>,
  duration: number | string | null,
): TrainingTypeStatus => {
  if (
    NO_TRAINING_TYPE_CATEGORIES.includes(service.category) ||
    !isTimedCategory(service.category)
  ) {
    return { created: false, reason: 'notApplicable' }
  }
  if (!service.icon || !service.color) return { created: false, reason: 'missingVisuals' }
  return { created: true, name: getTrainingTypeName(service.name, duration) }
}
